// Centralised client for the photohawk-serverless backend.
//
// Replaces the legacy per-action `axios.defaults.baseURL = …` pattern from
// photohawk-admin / photohawk-app with a single typed fetch wrapper that:
//   - targets the right service (admin / search / auth / workspaces / checkout),
//   - attaches the Cognito idToken as the raw `Authorization` header
//     (the convention the backend expects — no `Bearer` prefix),
//   - scopes calls to a tenant via the `hostname` query param,
//   - throws a typed error with the HTTP status on failure.
//
// See docs/migration/architecture-photohawk-serverless.md.

import {
  ADMIN_API_ENDPOINT,
  STUDIO_API_ENDPOINT,
  SEARCH_API_ENDPOINT,
  AUTH_API_ENDPOINT,
  WORKSPACES_API_ENDPOINT,
  CHECKOUT_API_ENDPOINT,
  BILLING_API_ENDPOINT,
  PLATFORM_API_ENDPOINT,
  FTPSERVER_API_ENDPOINT,
  ANALYTICS_API_ENDPOINT,
  FACES_API_ENDPOINT,
  SUPPORT_KB_API_ENDPOINT,
} from "@/lib/apiDomain";
import { cognitoGetSession } from "@/lib/cognito";

export type PhotohawkService =
  | "admin"
  | "studio"
  | "search"
  | "auth"
  | "workspaces"
  | "checkout"
  | "billing"
  | "platform"
  | "ftpserver"
  | "analytics"
  | "faces"
  | "kb";

const SERVICE_ENDPOINTS: Record<PhotohawkService, string> = {
  admin: ADMIN_API_ENDPOINT,
  studio: STUDIO_API_ENDPOINT,
  search: SEARCH_API_ENDPOINT,
  auth: AUTH_API_ENDPOINT,
  workspaces: WORKSPACES_API_ENDPOINT,
  checkout: CHECKOUT_API_ENDPOINT,
  billing: BILLING_API_ENDPOINT,
  platform: PLATFORM_API_ENDPOINT,
  ftpserver: FTPSERVER_API_ENDPOINT,
  analytics: ANALYTICS_API_ENDPOINT,
  faces: FACES_API_ENDPOINT,
  kb: SUPPORT_KB_API_ENDPOINT,
};

export class PhotohawkApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "PhotohawkApiError";
    this.status = status;
    this.body = body;
  }
}

/** A code rather than a sentence ("WHATSAPP_NUMBER_INVALID"): upper-case letters, digits and underscores. */
const isErrorCode = (v: string) => /^[A-Z][A-Z0-9_]{2,}$/.test(v);

/**
 * The text to show for an error response. Usually `error`; but when `error` is a machine code and the body carries a
 * plain `message` (e.g. a refused WhatsApp re-send: { error: "WHATSAPP_NUMBER_INVALID", message: "This number…" }),
 * the message. The code stays on the error's `body` for callers that branch on it.
 */
export const errorMessageOf = (parsed: unknown, status: number): string => {
  if (parsed && typeof parsed === "object") {
    const b = parsed as { error?: unknown; message?: unknown };
    const error = b.error == null ? undefined : String(b.error);
    const message = typeof b.message === "string" && b.message.trim() ? b.message.trim() : undefined;
    if (error && message && isErrorCode(error)) return message;
    if (error) return error;
    if (message) return message;
  }
  return `Request failed (${status})`;
};

export interface PhotohawkRequestOptions {
  service?: PhotohawkService;
  /** Query params; null/undefined entries are dropped. Array values are
   * appended as repeated keys (e.g. `content[]=a&content[]=b`). */
  params?: Record<string, string | number | boolean | null | undefined | string[]>;
  /** JSON request body (for POST/PUT). */
  body?: unknown;
  /** Tenant host (e.g. `acme.photohawk.com`) → sent as `?hostname=`. */
  hostname?: string;
  /** Skip attaching the Cognito idToken (for public endpoints). */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Give up after this long without a full answer: the request is aborted and fails with a 408 (transient,
   *  so callers that retry do). A fetch has no timeout of its own — a dead connection can hold it forever. */
  timeoutMs?: number;
}

/** Resolve the current Cognito idToken, or null if not signed in. `forceRefresh` mints a
 *  fresh pair from the refresh token even if the cached one still looks valid. */
const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const session = await cognitoGetSession(forceRefresh);
    return session?.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
};

/** A 401 that survived a forced token refresh: the sign-in itself is gone (refresh token
 *  expired or revoked), not just a stale id token. Callers doing long-running work (the
 *  uploader) pause and ask the user to sign in again rather than failing. */
export const isSessionExpired = (err: unknown): boolean =>
  err instanceof PhotohawkApiError && err.status === 401;

/** A failure worth retrying as-is after a pause: no response (network), timeout, rate
 *  limiting, or a server-side error. Anything else (400/403/404/413…) is permanent. */
export const isTransientApiError = (err: unknown): boolean => {
  if (!(err instanceof PhotohawkApiError)) return false;
  const s = err.status;
  return s === 0 || s === 408 || s === 425 || s === 429 || (s >= 500 && s <= 599);
};

const buildUrl = (
  endpoint: string,
  path: string,
  params?: PhotohawkRequestOptions["params"],
  hostname?: string,
): string => {
  const url = new URL(`${endpoint}${path.startsWith("/") ? path : `/${path}`}`);
  if (hostname) url.searchParams.set("hostname", hostname);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        for (const v of value) url.searchParams.append(key, String(v));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
};

async function request<T>(
  method: string,
  path: string,
  options: PhotohawkRequestOptions = {},
  forceRefresh = false,
): Promise<T> {
  const service = options.service ?? "admin";
  const endpoint = SERVICE_ENDPOINTS[service];
  const url = buildUrl(endpoint, path, options.params, options.hostname);

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  if (!options.anonymous) {
    const token = await getIdToken(forceRefresh);
    if (token) headers["Authorization"] = token;
  }

  // An optional timeout: our own controller, aborted by the caller's signal or the timer.
  let signal = options.signal;
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  if (options.timeoutMs && options.timeoutMs > 0) {
    const ac = new AbortController();
    const outer = options.signal;
    if (outer?.aborted) ac.abort();
    else outer?.addEventListener("abort", () => ac.abort(), { once: true });
    timer = setTimeout(() => { timedOut = true; ac.abort(); }, options.timeoutMs);
    signal = ac.signal;
  }

  let response: Response;
  let text: string;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal,
    });
    // Reading the body can fail too (the connection dropped mid-answer): that's a network error, not a bad answer.
    text = await response.text();
  } catch (err) {
    if (timedOut && !options.signal?.aborted) {
      throw new PhotohawkApiError(408, `Request timed out after ${Math.round((options.timeoutMs ?? 0) / 1000)} s`);
    }
    throw new PhotohawkApiError(
      0,
      err instanceof Error ? err.message : "Network request failed",
    );
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  // 401 = the gateway rejected the id token (expired between our check and the call, or
  // the cached session was stale). Mint a fresh one from the refresh token and retry
  // ONCE; a second 401 means the sign-in itself is gone (see isSessionExpired).
  if (response.status === 401 && !options.anonymous && !forceRefresh && !options.signal?.aborted) {
    return request<T>(method, path, options, true);
  }

  if (!response.ok) {
    throw new PhotohawkApiError(response.status, errorMessageOf(parsed, response.status), parsed);
  }

  return parsed as T;
}

export const photohawkApi = {
  get: <T>(path: string, options?: PhotohawkRequestOptions) =>
    request<T>("GET", path, options),
  post: <T>(path: string, options?: PhotohawkRequestOptions) =>
    request<T>("POST", path, options),
  put: <T>(path: string, options?: PhotohawkRequestOptions) =>
    request<T>("PUT", path, options),
  delete: <T>(path: string, options?: PhotohawkRequestOptions) =>
    request<T>("DELETE", path, options),
};
